import { localStorageValue } from "./LocalStorage"
import { Platform } from "./Platform"
import { IAccount, IAccountSubscriptionStatus } from "../Types"

// Whether the account's subscription has expired (lost access). Mirrors the server's
// Account.hasExpiredSubscription: a cancelled subscription keeps access until its paid
// period ends (nextBillingAt); an expired trial or a cancelled subscription past (or
// without) its billing date is expired. Everything else (active, running trial) is not.
export function subscriptionExpired(account?: IAccount): boolean {
  if (!account) {
    return false;
  }
  switch (account.subscriptionStatus) {
    case IAccountSubscriptionStatus.trialExpired:
      return true;
    case IAccountSubscriptionStatus.cancelled:
      return !account.nextBillingAt || Date.parse(account.nextBillingAt).valueOf() <= new Date().valueOf();
    default:
      return false;
  }
}

// Whether the native App Store in-app-purchase (StoreKit) flow should be shown
// instead of the Stripe checkout. It is on in the native iOS app
// (`appStyles` includes "ios"); in a cloud/browser session it stays behind the
// `logbook-inapp` localStorage opt-in so it can be exercised for testing without
// exposing it to regular web users.
export function iapPurchaseEnabled(app: { cloudSession: () => boolean; appStyles: string[] }): boolean {
  if (app.cloudSession()) {
    return localStorageValue("logbook-inapp") === "true";
  }
  return Platform.isIOS(app);
}

// Whether the subscription/plan view (AccountView) renders in-app rather than being
// opened on the server. It renders in-app for a cloud/browser session (the normal
// web app) and for the native iOS app (`appStyles` includes "ios", so the StoreKit
// purchase flow can run); a non-iOS native app (e.g. desktop) still opens the Stripe
// checkout on the server. This mirrors the `accountSubscription` route guard in
// AppView so the "view plans" action and the route it targets always agree.
export function subscriptionRendersInApp(app: { cloudSession: () => boolean; appStyles: string[] }): boolean {
  return app.cloudSession() || Platform.isIOS(app);
}

// Append the `inapp=true` opt-in to a logbook `/app/…` path so that a page opened in
// the system browser from the native iOS app renders the App-Store-compliant
// (IAP-only) view — the non-IAP Stripe purchase paths are hidden there. Only `/app/…`
// links carry this flag (the caller decides); it is idempotent and preserves any
// existing query string and hash fragment.
export function withInAppFlag(path: string): string {
  const hashIndex = path.indexOf("#");
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : "";
  const base = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  if (/(?:^|[?&])inapp=true(?:&|$)/.test(base)) {
    return path;
  }
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}inapp=true${hash}`;
}
